/**
 * The face that goes with an avatar id.
 *
 * The ids, their labels AND their glyphs are config — `config/profiles.json`,
 * reached through `client_config`. The table below is a **fallback for the
 * moment before that arrives**, not a second source of truth.
 *
 * It used to be the only source, and that broke the first time config grew: a
 * theme added six faces to `profiles.json`, and every one of them drew as a
 * card back on every screen because this file had never heard of them. The
 * same value in two places, found the usual way — by one of them changing.
 *
 * Why keep it at all: a client that has not fetched config yet still has to
 * draw somebody's seat, and a table of card backs for the first second is
 * worse than a table of the twelve faces that have always existed. Anything
 * added later is config-only and simply waits for the fetch, which is the
 * right trade — a fallback should cover the common case, not every case.
 */

const AVATAR_EMOJI: Record<string, string> = {
  octopus: '🐙',
  dino: '🦖',
  pizza: '🍕',
  alien: '👽',
  cowboy: '🤠',
  flamingo: '🦩',
  ghost: '👻',
  robot: '🤖',
  cat: '🐱',
  frog: '🐸',
  unicorn: '🦄',
  taco: '🌮',
};

/**
 * What the server actually says the faces are, once it has said so.
 *
 * Set once when `client_config` lands. A module-level cache rather than
 * threaded through, because `avatarEmoji` is called from the seat ring, the
 * lobby list, the picker and the chat bubbles, and passing config to every one
 * of them to answer "what does this id look like" is a lot of plumbing for a
 * lookup.
 */
let fromConfig: Record<string, string> = {};

export function rememberAvatars(avatars: readonly { id: string; emoji: string }[]): void {
  const next: Record<string, string> = {};
  for (const avatar of avatars) next[avatar.id] = avatar.emoji;
  fromConfig = next;
}

/** A card back stands in for an avatar nobody has heard of. */
export function avatarEmoji(avatarId: string): string {
  return fromConfig[avatarId] ?? AVATAR_EMOJI[avatarId] ?? '🂠';
}
